import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtHrs, formatDate, monthLabel, currentMonth } from "@/lib/timesheet";
import { usePersistedState } from "@/lib/usePersistedState";

const STATUS = {
  submitted: ["Submitted", "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300"],
  ready: ["Ready to submit", "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"],
  in_progress: ["In progress", "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300"],
};

export const lastMonths = (n) => {
  const [y, m] = currentMonth().split("-").map(Number);
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(y, m - 1 - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
};

export const MonthStatusCard = ({ refreshKey }) => {
  const months = useMemo(() => lastMonths(12), []);
  const [month, setMonth] = useState(months[0]);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = usePersistedState("approvals:monthStatusOpen", true);
  const idx = months.indexOf(month);
  const totals = useMemo(() => (rows || []).reduce((t, r) => ({
    approved: t.approved + r.weeks_approved, weeks: t.weeks + r.weeks_total,
    worked: t.worked + r.worked, leave: t.leave + r.leave,
    submitted: t.submitted + (r.status === "submitted" ? 1 : 0), invoices: t.invoices + (r.invoice_number ? 1 : 0),
  }), { approved: 0, weeks: 0, worked: 0, leave: 0, submitted: 0, invoices: 0 }), [rows]);

  useEffect(() => {
    setRows(null);
    api.get(`/month-status?month=${month}`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [month, refreshKey]);

  return (
    <Card className="mt-6 overflow-hidden" data-testid="month-status-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
        <button type="button" data-testid="month-status-toggle" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-left" aria-expanded={open}>
          <ChevronDown className={`h-5 w-5 transition-transform ${open ? "" : "-rotate-90"}`} />
          <h2 className="font-heading text-lg font-bold tracking-tight">Month-end status</h2>
          <span data-testid="month-status-label" className="whitespace-nowrap rounded-full bg-[#022a51] px-3 py-0.5 text-sm font-semibold text-white">{monthLabel(month)}</span>
        </button>
        <div className="flex items-center gap-1">
          <Button data-testid="month-status-prev" variant="ghost" size="icon" className="h-9 w-9" disabled={idx >= months.length - 1} onClick={() => setMonth(months[idx + 1])} aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></Button>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="month-status-picker" className="h-9 w-44"><SelectValue /></SelectTrigger>
            <SelectContent>{months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="month-status-next" variant="ghost" size="icon" className="h-9 w-9" disabled={idx <= 0} onClick={() => setMonth(months[idx - 1])} aria-label="Next month"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      {!open ? null : rows === null ? (
        <div className="flex justify-center py-10 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="py-10 text-center text-muted-foreground">No contractors to show.</div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contractor</TableHead>
                <TableHead>Weeks approved</TableHead>
                <TableHead className="text-right">Worked</TableHead>
                <TableHead className="text-right">Leave</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Invoice no.</TableHead>
                <TableHead>Submitted</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const [label, cls] = STATUS[r.status];
                return (
                  <TableRow key={r.user_id} data-testid={`month-status-row-${r.user_id}`}>
                    <TableCell><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{r.email}</div></TableCell>
                    <TableCell className="tabular" data-testid={`month-status-weeks-${r.user_id}`}>{r.weeks_approved} / {r.weeks_total}</TableCell>
                    <TableCell className="text-right font-semibold tabular">{fmtHrs(r.worked)}</TableCell>
                    <TableCell className="text-right tabular text-muted-foreground">{fmtHrs(r.leave)}</TableCell>
                    <TableCell>
                      <span data-testid={`month-status-badge-${r.user_id}`} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
                        {r.status === "submitted" && <Lock className="h-3 w-3" />}{label}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium" data-testid={`month-status-invoice-${r.user_id}`}>{r.invoice_number || "-"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.submitted_at ? formatDate(r.submitted_at) : "-"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter data-testid="month-status-totals">
              <TableRow className="font-semibold">
                <TableCell>Total <span className="font-normal text-muted-foreground">({rows.length} contractor{rows.length === 1 ? "" : "s"})</span></TableCell>
                <TableCell className="tabular" data-testid="month-status-total-weeks">{totals.approved} / {totals.weeks}</TableCell>
                <TableCell className="text-right tabular" data-testid="month-status-total-worked">{fmtHrs(totals.worked)}</TableCell>
                <TableCell className="text-right tabular" data-testid="month-status-total-leave">{fmtHrs(totals.leave)}</TableCell>
                <TableCell data-testid="month-status-total-submitted">{totals.submitted} of {rows.length} submitted</TableCell>
                <TableCell data-testid="month-status-total-invoices">{totals.invoices} invoice{totals.invoices === 1 ? "" : "s"}</TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      )}
    </Card>
  );
};
